import { Plus, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  availableSocialProfileTypes,
  canAddSocialProfile,
  defaultSocialProfileField,
  socialProfileFieldKey,
  type SocialProfileField,
  type SocialProfileType,
} from '@/lib/socialProfiles';

interface SocialProfilesEditorProps {
  value: SocialProfileField[];
  onChange: (next: SocialProfileField[]) => void;
}

export function SocialProfilesEditor({ value, onChange }: SocialProfilesEditorProps): JSX.Element {
  const { t } = useLanguage();
  const addableTypes = availableSocialProfileTypes(value);

  const handleAdd = () => {
    if (!canAddSocialProfile(value)) return;
    const nextType = addableTypes[0] ?? 'custom';
    onChange([...value, defaultSocialProfileField(nextType)]);
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleTypeChange = (index: number, type: SocialProfileType) => {
    onChange(
      value.map((row, i) =>
        i === index
          ? {
              type,
              required: row.required,
              ...(type === 'custom' ? { customLabel: row.customLabel ?? '' } : {}),
            }
          : row,
      ),
    );
  };

  const handleRequiredChange = (index: number, required: boolean) => {
    onChange(value.map((row, i) => (i === index ? { ...row, required } : row)));
  };

  const handleCustomLabelChange = (index: number, customLabel: string) => {
    onChange(value.map((row, i) => (i === index ? { ...row, customLabel } : row)));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs font-medium">{t('positions.socialProfiles')}</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 gap-1 text-xs"
          onClick={handleAdd}
          disabled={!canAddSocialProfile(value) || addableTypes.length === 0}
        >
          <Plus className="h-3 w-3" />
          {t('positions.addSocialProfile')}
        </Button>
      </div>

      {value.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">{t('positions.socialProfilesEmpty')}</p>
      ) : (
        <div className="space-y-2">
          {value.map((row, index) => {
            const rowKey = socialProfileFieldKey(row) || `row-${index}`;
            const typesForRow = availableSocialProfileTypes(
              value.filter((_, i) => i !== index),
            );
            const selectableTypes = typesForRow.includes(row.type)
              ? typesForRow
              : [row.type, ...typesForRow];

            return (
              <div key={rowKey} className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
                <Select
                  value={row.type}
                  onValueChange={(v) => handleTypeChange(index, v as SocialProfileType)}
                >
                  <SelectTrigger className="h-8 w-full min-w-[9rem] sm:w-[10.5rem] shrink-0 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {selectableTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {t(`positions.socialType.${type}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {row.type === 'custom' ? (
                  <Input
                    value={row.customLabel ?? ''}
                    onChange={(e) => handleCustomLabelChange(index, e.target.value)}
                    placeholder={t('positions.socialCustomLabel')}
                    className="h-8 flex-1 min-w-[8rem] text-xs"
                  />
                ) : (
                  <span className="hidden sm:inline flex-1 min-w-0 text-[11px] text-muted-foreground truncate">
                    {t(`positions.socialTypeHint.${row.type}`)}
                  </span>
                )}

                <div className="flex items-center gap-1.5 shrink-0">
                  <Label htmlFor={`social-required-${rowKey}`} className="text-[10px] text-muted-foreground">
                    {t('positions.socialRequired')}
                  </Label>
                  <Switch
                    id={`social-required-${rowKey}`}
                    checked={row.required}
                    onCheckedChange={(v) => handleRequiredChange(index, v)}
                  />
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={() => handleRemove(index)}
                  aria-label={t('positions.removeSocialProfile')}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
